import { useState, useEffect, useCallback } from 'react'
import { TermMonths } from '../utils/leasingFactors'

export interface HistoryEntry {
  id: string
  naw: number
  term: TermMonths
  monthlyRate: number
  factor: number
  timestamp: number
}

const STORAGE_KEY = 'pezula-calculator-history'
const MAX_HISTORY_ITEMS = 50

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

function loadFromStorage(): HistoryEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (error) {
    console.error('Failed to load history from localStorage:', error)
  }
  return []
}

function saveToStorage(history: HistoryEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history))
  } catch (error) {
    console.error('Failed to save history to localStorage:', error)
  }
}

export function useCalculatorHistory() {
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [isLoaded, setIsLoaded] = useState(false)

  // Load history from localStorage on mount
  useEffect(() => {
    const loaded = loadFromStorage()
    setHistory(loaded)
    setIsLoaded(true)
  }, [])

  // Save to localStorage whenever history changes (after initial load)
  useEffect(() => {
    if (isLoaded) {
      saveToStorage(history)
    }
  }, [history, isLoaded])

  const addEntry = useCallback(
    (naw: number, term: TermMonths, monthlyRate: number, factor: number) => {
      const newEntry: HistoryEntry = {
        id: generateId(),
        naw,
        term,
        monthlyRate,
        factor,
        timestamp: Date.now(),
      }

      setHistory((prev) => {
        const updated = [newEntry, ...prev]
        // Keep only the most recent entries
        return updated.slice(0, MAX_HISTORY_ITEMS)
      })
    },
    [],
  )

  const removeEntry = useCallback((id: string) => {
    setHistory((prev) => prev.filter((entry) => entry.id !== id))
  }, [])

  const clearHistory = useCallback(() => {
    setHistory([])
  }, [])

  return {
    history,
    addEntry,
    removeEntry,
    clearHistory,
    isLoaded,
  }
}
